import React, { useEffect, useRef, useState } from "react";
import { Modal, Button, Table } from "react-bootstrap";
import Swal from "sweetalert2";

import AttendanceServices from "../../../services/AttendanceServices";
import RequestServies from "../../../services/RequestServies";
import FilePreviewModal from "../Accounts/FilePreviewModal";

const formatDate = (value) => {
    if (!value) return "-";

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) return "-";

    return date.toLocaleString("en-GB", {
        timeZone: "Asia/Kolkata",
        hour12: false
    });
};

const imageUrl = (filename) => {
    if (!filename) return "";

    const base =
        window.location.hostname === "localhost"
            ? "http://localhost:3001"
            : "https://indigoinfra.in";

    return `${base}/img/attendance/${encodeURIComponent(filename)}`;
};

const ReviewAttendanceModal = ({
    show,
    handleClose,
    attendance,
    setRun,
    reviewType = "attendance"
}) => {
    const isDiesel = reviewType === "diesel";
    const title = isDiesel ? "Diesel" : "Attendance";

    const [record, setRecord] = useState(null);
    const [values, setValues] = useState({});
    const [editMode, setEditMode] = useState(false);
    const [busy, setBusy] = useState(false);
    const [stale, setStale] = useState(false);

    const [machineHistory, setMachineHistory] = useState([]);
    const [historyLoading, setHistoryLoading] = useState(false);
    const [historyError, setHistoryError] = useState("");

    const [previewFile, setPreviewFile] = useState(null);
    const [showPreview, setShowPreview] = useState(false);

    const actionLock = useRef(false);

    const fields = isDiesel
        ? [
            { key: "dieselInReading", label: "Diesel In" },
            { key: "dieselOutReading", label: "Diesel Out" }
        ]
        : [
            { key: "startKM", label: "Start HMR", photo: "start" },
            { key: "endKM", label: "End HMR", photo: "end" }
        ];

    const resetValues = (item) => {
        setValues({
            startKM: item?.startKM ?? "",
            endKM: item?.endKM ?? "",
            dieselInReading: item?.dieselInReading ?? "",
            dieselOutReading: item?.dieselOutReading ?? ""
        });
    };

    useEffect(() => {
        if (!show) return;

        setRecord(attendance);
        resetValues(attendance);
        setEditMode(false);
        setStale(false);
        setShowPreview(false);
        setPreviewFile(null);
    }, [show, attendance, reviewType]);

    useEffect(() => {
        let cancelled = false;

        setMachineHistory([]);
        setHistoryError("");
        setHistoryLoading(false);

        if (!show || !isDiesel || !attendance) {
            return;
        }

        const machine =
            attendance.vehicleId ||
            attendance.vehicle?._id;

        const store =
            attendance.vehicleStoreId ||
            attendance.vehicle?.asset?.store?._id;

        if (!machine || !store) {
            setHistoryError(
                "Machine or store details are unavailable for issue history."
            );
            return;
        }

        const loadHistory = async () => {
            setHistoryLoading(true);

            try {
                const response =
                    await RequestServies.machineDieselHistory({
                        store,
                        machine
                    });

                if (!Array.isArray(response?.data)) {
                    throw new Error("Unexpected diesel history response.");
                }

                if (!cancelled) {
                    setMachineHistory(response.data);
                }
            } catch (error) {
                if (!cancelled) {
                    setHistoryError(
                        error?.response?.data?.message ||
                        error.message ||
                        "Failed to load machine diesel history."
                    );
                }
            } finally {
                if (!cancelled) {
                    setHistoryLoading(false);
                }
            }
        };

        loadHistory();

        return () => {
            cancelled = true;
        };
    }, [show, isDiesel, attendance]);

    const status =
        (isDiesel
            ? record?.dieselApprovalStatus
            : record?.approvalStatus) || "pending";

    const canReview =
        record?.status === "checkOut" &&
        status === "pending" &&
        !stale;

    const refreshList = () => {
        setRun?.((previous) => previous + 1);
    };

    const close = () => {
        if (actionLock.current) return;

        // Refresh when closing, including after saved edits.
        refreshList();
        handleClose();
    };

    const showError = async (error) => {
        const statusCode = error?.response?.status;

        if (statusCode === 409 || statusCode === 400) {
            setStale(true);
        }

        await Swal.fire(
            "Error",
            error?.response?.data?.message ||
                error.message ||
                "Something went wrong.",
            "error"
        );
    };

    const saveChanges = async () => {
        if (actionLock.current || !canReview) return;

        const updates = {};

        for (const field of fields) {
            const value = values[field.key];

            if (
                String(value).trim() === "" ||
                !Number.isFinite(Number(value)) ||
                Number(value) < 0
            ) {
                await Swal.fire(
                    "Invalid Reading",
                    `${field.label} must be a valid nonnegative number.`,
                    "warning"
                );
                return;
            }

            updates[field.key] = Number(value);
        }

        if (!isDiesel && updates.endKM < updates.startKM) {
            await Swal.fire(
                "Invalid HMR",
                "End HMR must be greater than or equal to Start HMR.",
                "warning"
            );
            return;
        }

        actionLock.current = true;
        setBusy(true);

        try {
            const response =
                await AttendanceServices.updateReviewDetails(
                    record._id,
                    {
                        reviewType,
                        expectedVersion: record.__v ?? 0,
                        ...updates
                    }
                );

            if (!response?.data?._id) {
                throw new Error(
                    "Save response is incomplete. Close and reopen this review."
                );
            }

            // Keep populated machine details and the latest version.
            const updated = {
                ...record,
                ...response.data,
                vehicle: record.vehicle,
                driver: record.driver,
                store: record.store
            };

            setRecord(updated);
            resetValues(updated);
            setEditMode(false);

            await Swal.fire(
                "Saved",
                `${title} details updated successfully.`,
                "success"
            );
        } catch (error) {
            // A request can fail after the server has saved.
            setStale(true);
            await showError(error);
        } finally {
            actionLock.current = false;
            setBusy(false);
        }
    };

    const submitDecision = async (decision) => {
        if (
            actionLock.current ||
            !canReview ||
            editMode
        ) {
            return;
        }

        actionLock.current = true;
        setBusy(true);

        try {
            const rejecting = decision === "rejected";

            const result = await Swal.fire({
                title: `${rejecting ? "Reject" : "Approve"} ${title}?`,
                icon: rejecting ? "warning" : "question",
                showCancelButton: true,
                confirmButtonText: rejecting ? "Reject" : "Approve",
                ...(rejecting
                    ? {
                        input: "textarea",
                        inputLabel: "Rejection Remark",
                        inputPlaceholder: "Enter rejection remark...",
                        inputValidator: (value) =>
                            !value?.trim()
                                ? "Rejection remark is required."
                                : undefined
                    }
                    : {})
            });

            if (!result.isConfirmed) return;

            const version = record.__v ?? 0;

            if (isDiesel) {
                if (rejecting) {
                    await AttendanceServices.rejectDiesel(
                        record._id,
                        result.value.trim(),
                        version
                    );
                } else {
                    await AttendanceServices.approveDiesel(
                        record._id,
                        version
                    );
                }
            } else if (rejecting) {
                await AttendanceServices.rejectAttendance(
                    record._id,
                    result.value.trim(),
                    version
                );
            } else {
                await AttendanceServices.approveAttendance(
                    record._id,
                    version
                );
            }

            await Swal.fire(
                "Success",
                `${title} ${decision} successfully.`,
                "success"
            );

            refreshList();
            handleClose();
        } catch (error) {
            setStale(true);
            await showError(error);
        } finally {
            actionLock.current = false;
            setBusy(false);
        }
    };

    if (!record) return null;

    const asset = record.vehicle?.asset;
    const reviewRemark = isDiesel
        ? record.dieselApprovalRemark
        : record.approvalRemark;

    return (
        <>
            <Modal
                show={show}
                onHide={close}
                size="xl"
                centered
                enforceFocus={false}
                backdrop={busy ? "static" : true}
                keyboard={!busy}
            >
                <Modal.Header>
                    <Modal.Title>{title} Review</Modal.Title>

                    <button
                        type="button"
                        className="btn-close"
                        disabled={busy}
                        onClick={close}
                        aria-label="Close"
                    />
                </Modal.Header>

                <Modal.Body>
                    <div className="row g-3">
                        <div className="col-md-4">
                            <strong>Part No: </strong>
                            {asset?.masterItem?.partNo || "-"}
                        </div>

                        <div className="col-md-8">
                            <strong>Description: </strong>
                            {asset?.masterItem?.description ||
                                record.vehicleName ||
                                "-"}
                        </div>

                        <div className="col-md-4">
                            <strong>Equipment No: </strong>
                            {asset?.equipmentNo || record.equipmentNo || "-"}
                        </div>

                        <div className="col-md-4">
                            <strong>Driver: </strong>
                            {record.driver?.name || record.driverName || "-"}
                        </div>

                        <div className="col-md-4">
                            <strong>Store: </strong>
                            {record.store?.name ||
                                record.attendanceStore ||
                                "-"}
                        </div>

                        <div className="col-md-6">
                            <strong>Start: </strong>
                            {formatDate(record.startTime)}
                        </div>

                        <div className="col-md-6">
                            <strong>End: </strong>
                            {formatDate(record.endTime)}
                        </div>
                    </div>

                    <hr />

                    <div className="mb-3">
                        <strong>{title} Status: </strong>
                        <span className="text-capitalize">{status}</span>

                        {reviewRemark && (
                            <div className="mt-2">
                                <strong>Remark: </strong>
                                {reviewRemark}
                            </div>
                        )}
                    </div>

                    {stale && (
                        <div className="alert alert-warning">
                            Close and reopen this review to load the latest
                            record before making another change.
                        </div>
                    )}

                    <div className="row g-3">
                        {fields.map((field) => (
                            <div className="col-md-6" key={field.key}>
                                <label
                                    className="form-label"
                                    htmlFor={`${reviewType}-${field.key}`}
                                >
                                    {field.label}
                                </label>

                                <input
                                    id={`${reviewType}-${field.key}`}
                                    type="number"
                                    min="0"
                                    step="any"
                                    className="form-control"
                                    value={values[field.key] ?? ""}
                                    readOnly={!editMode}
                                    disabled={busy}
                                    onChange={(event) =>
                                        setValues((previous) => ({
                                            ...previous,
                                            [field.key]: event.target.value
                                        }))
                                    }
                                />

                                {field.photo &&
                                    record.meterPhotos?.[field.photo] && (
                                        <button
                                            type="button"
                                            className="btn btn-link btn-sm mt-1"
                                            disabled={busy}
                                            onClick={() => {
                                                setPreviewFile({
                                                    url: imageUrl(
                                                        record.meterPhotos[
                                                            field.photo
                                                        ]
                                                    )
                                                });
                                                setShowPreview(true);
                                            }}
                                        >
                                            View Meter Photo
                                        </button>
                                    )}
                            </div>
                        ))}
                    </div>

                    {isDiesel && (
                        <>
                            <hr />
                            <h5>Operator Diesel Fill-Ups</h5>

                            <Table bordered responsive>
                                <thead>
                                    <tr>
                                        <th>#</th>
                                        <th>Quantity</th>
                                        <th>HMR</th>
                                        <th>Date</th>
                                    </tr>
                                </thead>

                                <tbody>
                                    {record.desile?.length > 0 ? (
                                        record.desile.map((item, index) => (
                                            <tr key={item._id || index}>
                                                <td>{index + 1}</td>
                                                <td>{item.desileQty ?? "-"}</td>
                                                <td>{item.reading ?? "-"}</td>
                                                <td>
                                                    {formatDate(
                                                        item.diseleFilupDate
                                                    )}
                                                </td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan={4} className="text-center">
                                                No operator fill-ups found.
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </Table>

                            <h5 className="mt-3">
                                Machine Diesel Issue History
                            </h5>

                            {historyError && (
                                <div className="alert alert-warning">
                                    {historyError}
                                </div>
                            )}

                            <Table bordered striped responsive>
                                <thead>
                                    <tr>
                                        <th>#</th>
                                        <th>MIV No</th>
                                        <th>Part No</th>
                                        <th>Description</th>
                                        <th>Qty</th>
                                        <th>Current Stock</th>
                                        <th>Status</th>
                                        <th>Issued By</th>
                                        <th>Remark</th>
                                        <th>Date</th>
                                    </tr>
                                </thead>

                                <tbody>
                                    {historyLoading ? (
                                        <tr>
                                            <td colSpan={10} className="text-center">
                                                Loading...
                                            </td>
                                        </tr>
                                    ) : machineHistory.length > 0 ? (
                                        machineHistory.map((item, index) => (
                                            <tr key={item._id || index}>
                                                <td>{index + 1}</td>
                                                <td>{item.mivNo || "-"}</td>
                                                <td>{item.partNo || "-"}</td>
                                                <td>{item.description || "-"}</td>
                                                <td>{item.usedQty ?? "-"}</td>
                                                <td>{item.currentStock ?? "-"}</td>
                                                <td>
                                                    {item.mivStatus === "submit"
                                                        ? "Pending Approval"
                                                        : item.mivStatus === "close"
                                                            ? "Completed"
                                                            : item.mivStatus || "Open"}
                                                </td>
                                                <td>{item.userName || "-"}</td>
                                                <td>{item.remark || "-"}</td>
                                                <td>
                                                    {formatDate(item.createdAt)}
                                                </td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan={10} className="text-center">
                                                {historyError
                                                    ? "History unavailable."
                                                    : "No diesel issue history found."}
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </Table>
                        </>
                    )}
                </Modal.Body>

                <Modal.Footer>
                    <Button
                        variant="secondary"
                        disabled={busy}
                        onClick={close}
                    >
                        Close
                    </Button>

                    {canReview && (
                        <>
                            {editMode ? (
                                <>
                                    <Button
                                        variant="secondary"
                                        disabled={busy}
                                        onClick={() => {
                                            resetValues(record);
                                            setEditMode(false);
                                        }}
                                    >
                                        Cancel Edit
                                    </Button>

                                    <Button
                                        variant="primary"
                                        disabled={busy}
                                        onClick={saveChanges}
                                    >
                                        {busy ? "Saving..." : "Save Changes"}
                                    </Button>
                                </>
                            ) : (
                                <>
                                    <Button
                                        variant="primary"
                                        disabled={busy}
                                        onClick={() => setEditMode(true)}
                                    >
                                        Edit {isDiesel ? "Diesel" : "HMR"}
                                    </Button>

                                    <Button
                                        variant="danger"
                                        disabled={busy}
                                        onClick={() =>
                                            submitDecision("rejected")
                                        }
                                    >
                                        Reject {title}
                                    </Button>

                                    <Button
                                        variant="success"
                                        disabled={busy}
                                        onClick={() =>
                                            submitDecision("approved")
                                        }
                                    >
                                        Approve {title}
                                    </Button>
                                </>
                            )}
                        </>
                    )}
                </Modal.Footer>
            </Modal>

            <FilePreviewModal
                show={showPreview}
                handleClose={() => setShowPreview(false)}
                file={previewFile}
            />
        </>
    );
};

export default ReviewAttendanceModal;